import {
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Switch,
  Textarea,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  Flex,
  Box,
  Text,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState, useEffect } from 'react';
import { RiRobotLine, RiKeyLine, RiSettings4Line, RiEdit2Line } from 'react-icons/ri';
import { StyledSelect } from '../helivan/StyledSelect';
import { useUpdateCustomAgentMutation, CustomAgent } from '../../generated/graphql';

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

interface EditAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: CustomAgent;
  onSuccess: () => void;
}

export const EditAgentModal = ({
  isOpen,
  onClose,
  agent,
  onSuccess,
}: EditAgentModalProps) => {
  const toast = useToast();
  const [updateAgent, { loading }] = useUpdateCustomAgentMutation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    avatarUrl: '',
    apiEndpoint: '',
    apiAuthType: 'API_KEY',
    apiKey: '',
    systemPrompt: '',
    temperature: 0.7,
    maxTokens: 1000,
    snapshotEnabled: false,
  });

  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name,
        description: agent.description || '',
        avatarUrl: agent.avatarUrl || '',
        apiEndpoint: agent.apiEndpoint,
        apiAuthType: agent.apiAuthType,
        apiKey: '', // Don't show existing credentials
        systemPrompt: agent.systemPrompt || '',
        temperature: agent.temperature || 0.7,
        maxTokens: agent.maxTokens || 1000,
        snapshotEnabled: agent.snapshotEnabled,
      });
    }
  }, [agent]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!formData.apiEndpoint.trim()) {
      toast({
        title: 'API Endpoint is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      // Build credentials JSON if provided
      let apiCredentials: string | undefined;
      if (formData.apiKey) {
        if (formData.apiAuthType === 'API_KEY') {
          apiCredentials = JSON.stringify({ apiKey: formData.apiKey });
        } else if (formData.apiAuthType === 'BEARER') {
          apiCredentials = JSON.stringify({ token: formData.apiKey });
        } else if (formData.apiAuthType === 'OAUTH') {
          apiCredentials = JSON.stringify({ accessToken: formData.apiKey });
        }
      }

      await updateAgent({
        variables: {
          id: agent.id,
          input: {
            name: formData.name,
            description: formData.description || undefined,
            avatarUrl: formData.avatarUrl || undefined,
            apiEndpoint: formData.apiEndpoint,
            apiAuthType: formData.apiAuthType,
            apiCredentials,
            systemPrompt: formData.systemPrompt || undefined,
            temperature: formData.temperature,
            maxTokens: formData.maxTokens,
            snapshotEnabled: formData.snapshotEnabled,
          },
        },
      });

      toast({
        title: 'Agent updated',
        status: 'success',
        duration: 3000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: 'Error updating agent',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const canSubmit = formData.name.trim() && formData.apiEndpoint.trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" isCentered motionPreset="slideInBottom">
      <ModalOverlay
        bg="blackAlpha.600"
        backdropFilter="blur(10px)"
      />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={4}
        overflow="visible"
        maxH="90vh"
      >
        {/* Outer glow effect */}
        <Box
          position="absolute"
          top="-2px"
          left="-2px"
          right="-2px"
          bottom="-2px"
          borderRadius="28px"
          bg="linear-gradient(135deg, rgba(0,71,125,0.3) 0%, rgba(72,104,132,0.2) 50%, rgba(0,71,125,0.3) 100%)"
          backgroundSize="200% 200%"
          animation={`${gradientShift} 5s ease infinite`}
          filter="blur(8px)"
          opacity={0.6}
          zIndex={-1}
        />

        {/* Main modal container */}
        <Box
          bg="white"
          borderRadius="24px"
          overflow="hidden"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          display="flex"
          flexDirection="column"
          maxH="90vh"
        >
          {/* Header with gradient accent */}
          <Box position="relative" overflow="hidden" flexShrink={0}>
            {/* Gradient bar */}
            <Box
              h="4px"
              bgGradient="linear(to-r, #00477D, #486884, #00477D)"
              backgroundSize="200% 100%"
              animation={`${gradientShift} 3s ease infinite`}
            />

            <Flex
              px={6}
              pt={6}
              pb={4}
              align="center"
              gap={4}
            >
              {/* Icon with gradient background */}
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
                align="center"
                justify="center"
                boxShadow="0 4px 14px rgba(0,71,125,0.3)"
              >
                <RiEdit2Line size={28} color="white" />
              </Flex>

              <Box flex={1}>
                <Text
                  fontSize="20px"
                  fontWeight={700}
                  color="#1a1a2e"
                  letterSpacing="-0.02em"
                >
                  Edit Agent
                </Text>
                <Text
                  fontSize="14px"
                  color="#64748b"
                  mt={0.5}
                >
                  Update your agent configuration
                </Text>
              </Box>

              <ModalCloseButton
                position="relative"
                top={0}
                right={0}
                w="36px"
                h="36px"
                borderRadius="12px"
                bg="#f1f5f9"
                color="#64748b"
                _hover={{ bg: '#e2e8f0', color: '#1a1a2e' }}
                transition="all 0.2s"
              />
            </Flex>
          </Box>

          {/* Body */}
          <ModalBody px={6} py={5} overflowY="auto" flex={1}>
            <Stack spacing={5}>
              {/* Basic Information Section */}
              <Box
                bg="#f8fafc"
                borderRadius="16px"
                p={5}
                border="1px solid"
                borderColor="#e2e8f0"
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex
                    w="32px"
                    h="32px"
                    borderRadius="10px"
                    bg="rgba(0,71,125,0.1)"
                    align="center"
                    justify="center"
                  >
                    <RiRobotLine size={16} color="#00477D" />
                  </Flex>
                  <Text fontSize="15px" fontWeight={600} color="#1a1a2e">
                    Basic Information
                  </Text>
                </Flex>

                <Stack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Name
                    </FormLabel>
                    <Input
                      placeholder="Agent name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      size="lg"
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: '#00477D',
                        boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                      }}
                      transition="all 0.2s"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Description
                    </FormLabel>
                    <Textarea
                      placeholder="Describe what this agent does"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: '#00477D',
                        boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                      }}
                      transition="all 0.2s"
                      resize="none"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Avatar URL
                    </FormLabel>
                    <Input
                      placeholder="https://example.com/avatar.png"
                      value={formData.avatarUrl}
                      onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: '#00477D',
                        boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                      }}
                      transition="all 0.2s"
                    />
                  </FormControl>
                </Stack>
              </Box>

              {/* API Configuration Section */}
              <Box
                bg="#f8fafc"
                borderRadius="16px"
                p={5}
                border="1px solid"
                borderColor="#e2e8f0"
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex
                    w="32px"
                    h="32px"
                    borderRadius="10px"
                    bg="rgba(0,71,125,0.1)"
                    align="center"
                    justify="center"
                  >
                    <RiKeyLine size={16} color="#00477D" />
                  </Flex>
                  <Text fontSize="15px" fontWeight={600} color="#1a1a2e">
                    API Configuration
                  </Text>
                </Flex>

                <Stack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      {agent.connectionType === 'CONNECTOR' ? 'Gateway URL' : 'API Endpoint'}
                    </FormLabel>
                    <Input
                      placeholder={agent.connectionType === 'CONNECTOR'
                        ? "ws://127.0.0.1:18789"
                        : "https://api.example.com/agent"}
                      value={formData.apiEndpoint}
                      onChange={(e) => setFormData({ ...formData, apiEndpoint: e.target.value })}
                      size="lg"
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: '#00477D',
                        boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                      }}
                      transition="all 0.2s"
                    />
                  </FormControl>

                  <FormControl>
                    <StyledSelect
                      label="Authentication Type"
                      options={[
                        { value: 'API_KEY', label: 'API Key' },
                        { value: 'BEARER', label: 'Bearer Token' },
                        { value: 'OAUTH', label: 'OAuth' },
                      ]}
                      value={formData.apiAuthType}
                      onChange={(value) => setFormData({ ...formData, apiAuthType: value })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      {formData.apiAuthType === 'API_KEY'
                        ? 'API Key'
                        : formData.apiAuthType === 'BEARER'
                        ? 'Bearer Token'
                        : 'Access Token'}
                      {' '}
                      <Text as="span" fontWeight={400} color="#94a3b8" textTransform="none">
                        (leave blank to keep existing)
                      </Text>
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="Enter new credentials or leave blank"
                      value={formData.apiKey}
                      onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: '#00477D',
                        boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                      }}
                      transition="all 0.2s"
                    />
                  </FormControl>
                </Stack>
              </Box>

              {/* Advanced Settings Section */}
              <Box
                bg="#f8fafc"
                borderRadius="16px"
                p={5}
                border="1px solid"
                borderColor="#e2e8f0"
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex
                    w="32px"
                    h="32px"
                    borderRadius="10px"
                    bg="rgba(0,71,125,0.1)"
                    align="center"
                    justify="center"
                  >
                    <RiSettings4Line size={16} color="#00477D" />
                  </Flex>
                  <Text fontSize="15px" fontWeight={600} color="#1a1a2e">
                    Advanced Settings
                  </Text>
                </Flex>

                <Stack spacing={4}>
                  <FormControl>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      System Prompt
                    </FormLabel>
                    <Textarea
                      placeholder="Optional system prompt for the agent"
                      value={formData.systemPrompt}
                      onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                      rows={4}
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: '#00477D',
                        boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                      }}
                      transition="all 0.2s"
                      resize="none"
                    />
                  </FormControl>

                  <FormControl>
                    <Flex justify="space-between" mb={2}>
                      <Text
                        fontSize="13px"
                        fontWeight={600}
                        color="#475569"
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                      >
                        Temperature
                      </Text>
                      <Box
                        px={2}
                        py={0.5}
                        borderRadius="6px"
                        bg="rgba(0,71,125,0.1)"
                      >
                        <Text fontSize="13px" fontWeight={600} color="#00477D">
                          {formData.temperature}
                        </Text>
                      </Box>
                    </Flex>
                    <Slider
                      min={0}
                      max={2}
                      step={0.1}
                      value={formData.temperature}
                      onChange={(val) => setFormData({ ...formData, temperature: val })}
                    >
                      <SliderTrack bg="#e2e8f0" h="6px" borderRadius="full">
                        <SliderFilledTrack bgGradient="linear(135deg, #00477D 0%, #486884 100%)" borderRadius="full" />
                      </SliderTrack>
                      <SliderThumb
                        boxSize="18px"
                        border="2px solid"
                        borderColor="#00477D"
                        _focus={{ boxShadow: '0 0 0 3px rgba(0,71,125,0.2)' }}
                      />
                    </Slider>
                    <HStack justify="space-between" mt={1}>
                      <Text fontSize="12px" color="#94a3b8">Precise (0)</Text>
                      <Text fontSize="12px" color="#94a3b8">Creative (2)</Text>
                    </HStack>
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Max Tokens
                    </FormLabel>
                    <NumberInput
                      min={100}
                      max={4000}
                      value={formData.maxTokens}
                      onChange={(_, val) => setFormData({ ...formData, maxTokens: val || 1000 })}
                    >
                      <NumberInputField
                        bg="white"
                        border="2px solid"
                        borderColor="#e2e8f0"
                        borderRadius="14px"
                        fontSize="15px"
                        color="#1a1a2e"
                        _hover={{ borderColor: '#cbd5e1' }}
                        _focus={{
                          borderColor: '#00477D',
                          boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Text
                        fontSize="13px"
                        fontWeight={600}
                        color="#475569"
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                      >
                        Enable State Snapshot
                      </Text>
                      <Text fontSize="12px" color="#94a3b8" mt={0.5}>
                        Saves a snapshot of this agent's configuration that can be restored later.
                      </Text>
                    </Box>
                    <Switch
                      isChecked={formData.snapshotEnabled}
                      onChange={(e) => setFormData({ ...formData, snapshotEnabled: e.target.checked })}
                      colorScheme="blue"
                      size="md"
                    />
                  </FormControl>
                </Stack>
              </Box>
            </Stack>
          </ModalBody>

          {/* Footer */}
          <Flex
            px={6}
            py={5}
            bg="#f8fafc"
            borderTop="1px solid"
            borderColor="#e2e8f0"
            gap={3}
            justify="flex-end"
            flexShrink={0}
          >
            <Box
              as="button"
              px={5}
              py={2.5}
              borderRadius="12px"
              fontSize="14px"
              fontWeight={600}
              color="#64748b"
              bg="white"
              border="1px solid"
              borderColor="#e2e8f0"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={onClose}
            >
              Cancel
            </Box>
            <Box
              as="button"
              px={6}
              py={2.5}
              borderRadius="12px"
              fontSize="14px"
              fontWeight={600}
              color="white"
              bgGradient="linear(135deg, #00477D 0%, #1a5a94 100%)"
              cursor={loading || !canSubmit ? 'not-allowed' : 'pointer'}
              opacity={loading || !canSubmit ? 0.6 : 1}
              transition="all 0.2s"
              boxShadow="0 2px 8px rgba(0,71,125,0.3)"
              _hover={!loading && canSubmit ? {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
              } : {}}
              _active={!loading && canSubmit ? { transform: 'scale(0.98)' } : {}}
              onClick={!loading && canSubmit ? handleSubmit : undefined}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Box>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};
